// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// third party imports
import notify from 'devextreme/ui/notify';
import * as moment from 'moment-timezone';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { ARConstants } from '../../../cirabooks-module/_constants/cb-ar-constants';

// Service imports
import { UserService } from '../../../../_services/data-services/user.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { JournalEntryDetailService } from '../../_services/general-ledger/journal-entry-detail.service';

// Model imports
import { JournalEntryDetailParameterDto } from '../../_models/general-ledger/journal-entry/journal-entry-detail-parameter-dto';
import { JournalEntryDetailsResultDto } from '../../_models/general-ledger/journal-entry/journal-entry-details-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { JournalEntryAttachmentUpdateParametersDto } from '../../_models/general-ledger/journal-entry/journal-entry-attachment-update-parameters-dto';
import { GLJournalAttachmentRawDto } from '../../_models/general-ledger/journal-entry/gl-journal-attachment-raw-dto';
import { DimensionOptionResultDto } from '../../_models/common/dimensions/dimension-option-result-dto';
import { AttachmentRawDto } from '../../../../management-common/_models/attachment-raw-dto';

@Component({
    selector: 'app-journal-entry-detail',
    templateUrl: './journal-entry-detail.component.html',
    styleUrls: ['./journal-entry-detail.component.scss']
})
export class JournalEntryDetailComponent implements OnInit {
    @ViewChild('journalEntryMessageBox', { static: false }) journalEntryMessageBox: MessageBoxComponent;

    @Input() visible: boolean;
    @Input() journalEntryParam: JournalEntryDetailParameterDto;
    @Output() closeJournalEntryDetail = new EventEmitter<boolean>();

    loadingVisible = false;
    defaultLabelLocation: string;
    isInternalUser = false;
    currencyFormat: any;
    dateFormat: string;

    journalEntryDetailData: JournalEntryDetailsResultDto;
    dimensionSource: DimensionOptionResultDto[] = [];
    entityUsesSubcategory: boolean;

    cashOrAccrualOptions = [
        { key: 'Both', displayText: 'Cash and Accrual' },
        { key: 'Cash', displayText: 'Cash' },
        { key: 'Accrual', displayText: 'Accrual' }
    ];
    cashOrAccrualCodeObj: any;

    transactionLogResultData: TransactionLogResultDto[] = [];
    displayTransactionLogPopUp = false;
    transactionLogPopupTitle: string;
    communityId: number;

    journalEntryAttachments: Array<AttachmentRawDto> = [];
    jeAttachments: Array<GLJournalAttachmentRawDto> = [];

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    iconHistory = IconConstants.HISTORY;
    iconPeperClip = IconConstants.ATTACHMENT;
    iconEdit = IconConstants.EDIT;

    constructor(
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly detailPopupService: DetailPopupsService,
        private readonly journalEntryDetailService: JournalEntryDetailService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.dateFormat = StringFormatConstants.dateFormat;
    }

    ngOnInit() {
        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        this.isInternalUser = this.userService.isInternal();
        this.journalEntryDetailData = new JournalEntryDetailsResultDto();
        this.journalEntryDetailData.attachments = [];
        if (!!this.journalEntryParam) {
            this.communityId = this.journalEntryParam.communityId;
            this.populateJournalEntryDetail(true);
        }
    }

    onHidden(): void {
        this.visible = false;
        this.closeJournalEntryDetail.emit(false);
    }

    onHistoryClick(): void {
        this.transactionLogPopupTitle = `Log of GL Journal Entry ${this.journalEntryParam.journalEntryId}`;
        const param: JournalEntryDetailParameterDto = new JournalEntryDetailParameterDto();
        param.communityId = this.journalEntryParam.communityId;
        param.managementCompanyId = this.journalEntryParam.managementCompanyId;
        param.journalEntryId = this.journalEntryParam.journalEntryId;
        param.clearCache = true;
        this.loadingVisible = true;
        this.journalEntryDetailService.getJournalEntryDetailHistory(param).subscribe(
            (result) => {
                this.transactionLogResultData = result;
                this.displayTransactionLogPopUp = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onSaveAttachmentClick(): void {
        const param: JournalEntryAttachmentUpdateParametersDto = new JournalEntryAttachmentUpdateParametersDto();
        param.communityId = this.journalEntryParam.communityId;
        param.managementCompanyId = this.journalEntryParam.managementCompanyId;
        param.journalEntryId = this.journalEntryParam.journalEntryId;
        param.attachments = this.jeAttachments;
        this.loadingVisible = true;
        this.journalEntryDetailService.updateJEAttachments(param).subscribe(
            () => {
                notify('Attachment successfully updated.', 'success', MessageInterval.successMsgInterval);
                this.populateJournalEntryDetail(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onViewDepositClick(): void {
        const jeParam = this.journalEntryParam;
        if (Utils.notNullAndDefined(jeParam)) {
            this.detailPopupService.updateSubjectDepositDetail(
                jeParam.managementCompanyId,
                jeParam.communityId,
                this.journalEntryDetailData.sourceReferenceId
            );
        }
    }

    onEditOrDuplicateJEClick(isDuplicateAction = false): void {
        this.detailPopupService.updateSubjectJournalEntryEditor(
            this.journalEntryParam.managementCompanyId,
            this.journalEntryParam.communityId,
            this.journalEntryParam.journalEntryId,
            isDuplicateAction
        );
        this.onHidden();
    }

    onAttachmentUpdate(attachments: Array<AttachmentRawDto>): void {
        this.jeAttachments = [];
        attachments.forEach((attachment) => {
            const obj: GLJournalAttachmentRawDto = new GLJournalAttachmentRawDto();
            obj.crudAction = attachment.action;
            obj.fileDataBase64 = attachment.fileBase64String;
            obj.fileStoreID = attachment.fileId;
            obj.fileName = attachment.fileName;
            if (Utils.notNullAndDefined(obj.crudAction)) {
                this.jeAttachments.push(obj);
            }
        });
    }

    hasDepositPopup(): boolean {
        if (
            Utils.notNullAndDefined(this.journalEntryDetailData) &&
            Utils.notNullOrEmpty(this.journalEntryDetailData.sourceReferenceType)
        ) {
            return (
                this.detailPopupService.hasDetailsPopup('CM', this.journalEntryDetailData.sourceReferenceType) &&
                Utils.isTrue(this.journalEntryDetailData.allowViewDeposit)
            );
        } else {
            return false;
        }
    }

    private populateJournalEntryDetail(clearCache: boolean): void {
        const param: JournalEntryDetailParameterDto = new JournalEntryDetailParameterDto();
        param.communityId = this.journalEntryParam.communityId;
        param.managementCompanyId = this.journalEntryParam.managementCompanyId;
        param.journalEntryId = this.journalEntryParam.journalEntryId;
        param.clearCache = clearCache;
        this.loadingVisible = true;
        this.journalEntryDetailService.getJournalEntryDetails(param).subscribe(
            (result: JournalEntryDetailsResultDto) => {
                this.journalEntryDetailData = result;

                this.entityUsesSubcategory = this.journalEntryDetailData.entityUsesSubCategory;
                this.cashOrAccrualOptions.forEach((e) => {
                    if (e.key === this.journalEntryDetailData.cashOrAccrual) {
                        this.cashOrAccrualCodeObj = e;
                    }
                });

                this.journalEntryDetailData.items.forEach((amount) => {
                    if (amount.debitOrCredit === ARConstants.CREDIT_CODE) {
                        amount.credit = amount.amount;
                    } else if (amount.debitOrCredit === ARConstants.DEBIT_CODE) {
                        amount.debit = amount.amount;
                    }
                });

                if (Utils.notNullAndDefined(this.journalEntryDetailData.deletedDate)) {
                    this.journalEntryMessageBox.showInfoMessage(
                        'Information',
                        `This Journal Entry has been deleted on ${moment(
                            this.journalEntryDetailData.deletedDate
                        ).format('MM/DD/YYYY')}`
                    );
                }

                if (Utils.isTrue(this.journalEntryDetailData.allowAttachment)) {
                    this.prepareAttachments();
                }

                this.prepareDimensions();
                this.loadingVisible = false;
            },
            (error) => {
                this.onHidden();
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareAttachments(): void {
        this.journalEntryAttachments = [];
        if (Utils.notNullOrEmpty(this.journalEntryDetailData.attachments)) {
            this.journalEntryDetailData.attachments.forEach((attachment) => {
                const obj: AttachmentRawDto = new AttachmentRawDto();
                obj.fileId = attachment.attachmentId;
                obj.fileName = attachment.fileName;
                obj.fileUrl = attachment.fileUrl;

                this.journalEntryAttachments.push(obj);
            });
        }
    }

    private prepareDimensions(): void {
        this.journalEntryDetailData.items.forEach((item) => {
            this.dimensionSource.push(...item.dimensions);
        });

        this.dimensionSource = this.dimensionSource.filter(
            (
                (s) => (a) =>
                    !s.has(a.dimensionCode) && s.add(a.dimensionCode)
            )(new Set())
        );
    }
}
